import { useEffect, useState } from 'react';
import { combineLatest, from } from 'rxjs';
import { distinctBy } from '../../utility';
import { useTranslation } from 'react-i18next';
import {
  ITranslationDetailProps,
  PriceRender,
  StyledDialog,
  TranslationDetail,
} from '../../ui';
import { IOfferTreatmentDto } from '../../model/IOfferTreatmentDto';
import { ITreatmentApi, TreatmentApi } from '../../services/treatmentApi';
import { ITreatmentDto } from '../../model/ITreatmentDto';

export interface IOfferTreatmentDisplayProps {
  offerTreatments: IOfferTreatmentDto[];
}

export function OfferTreatmentDisplay(props: IOfferTreatmentDisplayProps) {
  const { t, i18n } = useTranslation('offer-package');
  const api: ITreatmentApi = new TreatmentApi();
  const { offerTreatments } = props;

  const [treatments, setTreatments] = useState<ITreatmentDto[]>();
  const [modalContent, setModalContent] = useState<ITranslationDetailProps>();

  useEffect(() => {
    const idsToLoad = [...distinctBy(offerTreatments, (e) => e.treatmentId)]
      .map((e) => e.treatmentId)
      .map((e) => from(api.getById(e)));

    combineLatest(idsToLoad).subscribe({
      next: (data) => setTreatments(data),
    });
  }, [offerTreatments]);

  if (!treatments) {
    return <p>Loading...</p>;
  }

  const renderTreatment = (id: number) => {
    const treatment = treatments?.find((e) => e.id === id);
    if (!treatment) {
      return '';
    }

    const translation = treatment?.treatmentTranslationList.find(
      (e) => e.langCode === i18n.language
    );

    return translation?.title;
  };

  return (
    <div>
      <StyledDialog
        isOpen={modalContent !== undefined}
        onClose={() => setModalContent(undefined)}
      >
        {modalContent && (
          <TranslationDetail
            text={modalContent.text}
            imageLinks={modalContent.imageLinks}
          ></TranslationDetail>
        )}
      </StyledDialog>

      <h3 className="text-lg bg-brand-green-700 text-white -mx-4 px-4 py-2">
        {t('treatment.title')}
      </h3>

      <ul className="-mx-4 divide-y">
        {offerTreatments.map((offerTreatment) => (
          <li key={offerTreatment.id}>
            <button
              type="button"
              className="flex group justify-between items-center w-full gap-2 px-6 hover:bg-slate-100 py-2 print:p-0"
              onClick={() => {
                const roomCategory = treatments?.find(
                  (e) => e.id === offerTreatment.treatmentId
                );
                const translation = roomCategory?.treatmentTranslationList.find(
                  (e) => e.langCode === i18n.language
                );

                if (translation) {
                  setModalContent({
                    text: translation,
                    imageLinks: roomCategory?.imageLinks ?? [],
                  });
                }
              }}
            >
              <div className="text-left">
                <p className="font-bold">
                  {renderTreatment(offerTreatment.treatmentId)}
                </p>
                <p className="group-hover:underline mt-2">
                  <strong>{t('shared.more')}...</strong>
                </p>
              </div>

              <div className="text-right">
                <PriceRender
                  price={offerTreatment.price}
                  offerPrice={offerTreatment.offerPrice}
                ></PriceRender>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default OfferTreatmentDisplay;
